/**
 * Pendle V2 public market listing.
 *
 * ONE un-paginated endpoint returns every Pendle market on every chain,
 * including the per-market `details` block (implied APY, pool liquidity, TVL).
 * That is the whole data source for this provider — no multicall, no subgraph,
 * no API key.
 *
 *   https://api-v2.pendle.finance/core/v1/markets/all
 *
 * There is also a per-chain `/v1/{chainId}/markets/active`, which pre-filters
 * to live markets. We deliberately do NOT use it: it costs one request per
 * chain for the same data, and — more importantly — expiry has to be judged
 * here anyway (see {@link isLiveMarket}), so taking the filter from the API
 * would just move the one rule this provider must never get wrong out of our
 * code and into someone else's.
 *
 * Docs: https://docs.pendle.finance/pendle-v2/introduction
 */

export const PENDLE_MARKETS_URL =
  'https://api-v2.pendle.finance/core/v1/markets/all'

/**
 * Token metadata for every Pendle-known asset on every chain — decimals,
 * symbol, name, icon. One global call, same as the markets listing.
 *
 * It exists here because `/markets/all` publishes addresses but NO decimals,
 * and a PT's decimals cannot be inferred from its underlying's (they disagree
 * on ~16 % of live markets). The alternative — reading `decimals()` on-chain
 * for every PT — makes the whole provider depend on a ~110-call multicall on
 * Ethereum, and when that multicall fails viem returns `'0x'` per call rather
 * than throwing, so the entire chain's listing silently disappears. Observed,
 * not hypothesised. An HTTP source that fails loudly is the better dependency.
 */
export const PENDLE_ASSETS_URL =
  'https://api-v2.pendle.finance/core/v1/assets/all'

/**
 * Chains with Pendle deployments. The endpoint is global (it returns every
 * chain regardless), so this set exists only to skip the round-trip on chains
 * Pendle does not cover — the parser is chain-agnostic and a new chain appears
 * as soon as it is added here.
 */
export const PENDLE_CHAIN_IDS = new Set<string>([
  '1', // Ethereum
  '10', // OP Mainnet
  '56', // BNB Chain
  '143', // Monad
  '146', // Sonic
  '999', // HyperEVM
  '5000', // Mantle
  '8453', // Base
  '9745', // Plasma
  '42161', // Arbitrum One
  '80094', // Berachain
])

export const hasPendleMarkets = (chainId: string): boolean =>
  PENDLE_CHAIN_IDS.has(String(chainId))

/**
 * The `details` block. Every field is a FRACTION (`0.0721` = 7.21 %) except
 * the three USD amounts.
 *
 * **Only `impliedApy` describes what a PT holder earns.** The others are
 * different products' numbers sharing one object, and picking the wrong one is
 * the same class of error as TermMax's maker-side `apr()` naming:
 *
 *  - `impliedApy`    — the fixed yield locked in by buying PT and holding to
 *                      maturity. THE PT RATE.
 *  - `underlyingApy` — the SY's own floating yield. What the YT side earns,
 *                      and what PT holders give up.
 *  - `pendleApy`     — PENDLE emissions paid to LPs. Not to PT holders.
 *  - `aggregatedApy` / `maxBoostedApy` / `swapFeeApy` — LP-side returns.
 */
export interface PendleApiMarketDetails {
  /** AMM pool depth, USD. What can actually be traded in or out. */
  liquidity?: number | null
  /** Whole-market TVL, USD (pool + SY backing). */
  totalTvl?: number | null
  tradingVolume?: number | null
  /** Fixed yield to maturity, as a FRACTION. The PT rate. */
  impliedApy?: number | null
  /** The SY's floating yield, as a FRACTION. NOT the PT rate. */
  underlyingApy?: number | null
  /** PENDLE emissions to LPs, as a FRACTION. NOT the PT rate. */
  pendleApy?: number | null
  /** LP total, as a FRACTION. NOT the PT rate. */
  aggregatedApy?: number | null
  maxBoostedApy?: number | null
  swapFeeApy?: number | null
  /** AMM swap fee, as a FRACTION of the traded amount. */
  feeRate?: number | null
  yieldRange?: { min?: number | null; max?: number | null } | null
}

/**
 * One `markets[]` entry.
 *
 * **`pt` / `yt` / `sy` / `underlyingAsset` are `"<chainId>-<address>"`**, not
 * bare addresses — see {@link splitChainScopedAddress}. `expiry` is ISO-8601,
 * not a unix stamp (the token list carries the unix form).
 */
export interface PendleApiMarket {
  /** Underlying's display name, e.g. `wstETH` — NOT the PT symbol. */
  name?: string | null
  /** The AMM market contract, a bare address. */
  address?: string | null
  /** ISO-8601. */
  expiry?: string | null
  /** `"1-0xb253…"` */
  pt?: string | null
  yt?: string | null
  sy?: string | null
  underlyingAsset?: string | null
  accountingAsset?: string | null
  protocol?: string | null
  icon?: string | null
  details?: PendleApiMarketDetails | null
  isNew?: boolean | null
  isPrime?: boolean | null
  categoryIds?: string[] | null
  chainId?: number | null
}

/**
 * Split Pendle's `"<chainId>-<address>"` composite into its parts.
 *
 * Returns `undefined` for anything that is not that shape — a bare address
 * included. Pendle has never returned one, and silently accepting it would
 * mean guessing the chain, which is how a market gets attributed to the wrong
 * network.
 */
export function splitChainScopedAddress(
  value: string | null | undefined,
): { chainId: string; address: string } | undefined {
  if (!value) return undefined
  const idx = value.indexOf('-')
  if (idx <= 0) return undefined
  const chainId = value.slice(0, idx)
  const address = value.slice(idx + 1).toLowerCase()
  if (!/^\d+$/.test(chainId)) return undefined
  if (!/^0x[0-9a-f]{40}$/.test(address)) return undefined
  return { chainId, address }
}

/** Parse the ISO-8601 `expiry` to unix SECONDS. `undefined` when unparseable. */
export function parseExpirySeconds(
  expiry: string | null | undefined,
): number | undefined {
  if (!expiry) return undefined
  const ms = Date.parse(expiry)
  if (!Number.isFinite(ms)) return undefined
  return Math.floor(ms / 1000)
}

/**
 * Is this market still live?
 *
 * **Judged against the clock, every time — never off a cached flag.** The
 * token list carries a `props.pendle.expired` boolean that is only as fresh as
 * the last regeneration, and an expired PT that keeps showing a pre-expiry
 * fixed APY is precisely the bug yield-tracer migration 0088 had to clean up
 * after. A market with no parseable expiry is treated as NOT live: an
 * unbounded fixed-rate row is never the safe default.
 */
export function isLiveMarket(
  market: PendleApiMarket,
  nowSecs: number = Math.floor(Date.now() / 1000),
): boolean {
  const expiry = parseExpirySeconds(market.expiry)
  if (expiry === undefined) return false
  return expiry > nowSecs
}

/** One `assets[]` entry from {@link PENDLE_ASSETS_URL}. */
export interface PendleApiAsset {
  chainId?: number | null
  address?: string | null
  symbol?: string | null
  name?: string | null
  decimals?: number | null
  /** `['PT']`, `['YT']`, `['SY']`, … */
  tags?: string[] | null
  expiry?: string | null
  proIcon?: string | null
}

/**
 * In-isolate cache for a global listing.
 *
 * `getVaultPublicDataAll` runs per chain, so a worker serving 7 Pendle chains
 * would otherwise issue 7 identical requests for one global payload. The
 * PROMISE (not the result) is cached, so concurrent chain fetches share a
 * single in-flight request rather than racing, and a rejection evicts itself
 * so one failure does not poison the next 60 s.
 */
const CACHE_TTL_MS = 60_000

interface Cached<T> {
  at: number
  promise: Promise<T>
}

let marketsCache: Cached<PendleApiMarket[]> | undefined
let assetsCache: Cached<Map<string, PendleApiAsset>> | undefined

/** Drop both cached listings. Tests only. */
export function clearPendleMarketsCache(): void {
  marketsCache = undefined
  assetsCache = undefined
}

async function getJson(url: string, label: string): Promise<any> {
  const response = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!response.ok) {
    throw new Error(
      `Pendle ${label} API failed: ${response.status} - ${response.statusText}`,
    )
  }
  return response.json()
}

function cache<T>(
  slot: Cached<T> | undefined,
  build: () => Promise<T>,
  store: (c: Cached<T> | undefined) => void,
): Promise<T> {
  const now = Date.now()
  if (slot && now - slot.at < CACHE_TTL_MS) return slot.promise
  const promise = build()
  const entry: Cached<T> = { at: now, promise }
  store(entry)
  promise.catch(() => store(undefined))
  return promise
}

/**
 * Fetch the global Pendle market listing (all chains, live and expired).
 *
 * Chain filtering, expiry filtering and normalization happen in `fetchPublic`.
 */
export function fetchPendleApiMarkets(): Promise<PendleApiMarket[]> {
  return cache(
    marketsCache,
    async () =>
      ((await getJson(PENDLE_MARKETS_URL, 'markets'))?.markets ??
        []) as PendleApiMarket[],
    (c) => {
      marketsCache = c
    },
  )
}

/** `"<chainId>-<lowercased address>"` — the key both Pendle listings use. */
export const assetKey = (chainId: string | number, address: string): string =>
  `${chainId}-${address.toLowerCase()}`

/**
 * Fetch Pendle's global asset metadata, keyed by {@link assetKey}.
 *
 * Only called when something is missing from the caller's token list, so a
 * fully-hydrated caller pays nothing for it.
 */
export function fetchPendleApiAssets(): Promise<Map<string, PendleApiAsset>> {
  return cache(
    assetsCache,
    async () => {
      const json = await getJson(PENDLE_ASSETS_URL, 'assets')
      const map = new Map<string, PendleApiAsset>()
      for (const a of (json?.assets ?? []) as PendleApiAsset[]) {
        if (a?.chainId == null || !a?.address) continue
        map.set(assetKey(a.chainId, a.address), a)
      }
      return map
    },
    (c) => {
      assetsCache = c
    },
  )
}
